// context/UserAccessContext.js — Role-based access control for the app
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../services/msalService";
import {
  fetchUserAccessList,
  createUserEntry,
  updateUserEntry,
  deleteUserEntry,
} from "../services/userAccessService";

const STORAGE_KEY = "lekoil_user_access";
// editor sits between staff and manager so they have AT-admin powers but not app-wide manager access
const ROLE_LEVELS = { admin: 4, manager: 3, editor: 2.5, staff: 2, viewer: 1 };

const SEED_DEMO_USERS = [
  { id: "demo-1", email: "admin@lekoil.com", name: "SCM Admin", role: "admin", department: "Supply Chain", isActive: true, grantedBy: "system", grantedAt: new Date().toISOString(), notes: "System administrator" },
  { id: "demo-2", email: "manager@lekoil.com", name: "Procurement Manager", role: "manager", department: "Procurement", isActive: true, grantedBy: "admin@lekoil.com", grantedAt: new Date().toISOString(), notes: "" },
  { id: "demo-3", email: "staff@lekoil.com", name: "SCM Officer", role: "staff", department: "Logistics", isActive: true, grantedBy: "admin@lekoil.com", grantedAt: new Date().toISOString(), notes: "" },
  { id: "demo-4", email: "viewer@lekoil.com", name: "Finance Reviewer", role: "viewer", department: "Finance", isActive: true, grantedBy: "manager@lekoil.com", grantedAt: new Date().toISOString(), notes: "Read-only access" },
];

export const UserAccessContext = createContext(null);

export function useUserAccess() {
  const ctx = useContext(UserAccessContext);
  if (!ctx) throw new Error("useUserAccess must be used inside UserAccessProvider");
  return ctx;
}

function loadLocalUsers() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  return SEED_DEMO_USERS;
}

function saveLocalUsers(users) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(users)); } catch {}
}

export function UserAccessProvider({ children }) {
  const { instance, accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username || accounts[0]?.name || "";
  const currentUserName = accounts[0]?.name || "Current User";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(true);
  const [spError, setSpError] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setSpError(null);
    try {
      const token = await getAccessToken(instance, accounts);
      const list = await fetchUserAccessList(token);
      // Bootstrap first admin if list is empty
      if (list.length === 0 && currentUserEmail) {
        const firstAdmin = {
          email: currentUserEmail,
          name: currentUserName,
          role: "admin",
          department: "Supply Chain",
          isActive: true,
          grantedBy: "system",
          grantedAt: new Date().toISOString(),
          notes: "Auto-provisioned as first administrator",
        };
        const created = await createUserEntry(token, firstAdmin);
        setUsers([created]);
      } else {
        setUsers(list);
      }
      setIsDemoMode(false);
    } catch (err) {
      console.error("[UserAccess] SharePoint connection failed:", err.message);
      setSpError(err.message);
      // Fall back to localStorage in demo mode
      const local = loadLocalUsers();
      // Ensure current user is in the list as admin in demo mode
      const found = local.find((u) => u.email.toLowerCase() === currentUserEmail.toLowerCase());
      if (!found && currentUserEmail) {
        const meAsAdmin = {
          id: `local-${Date.now()}`,
          email: currentUserEmail,
          name: currentUserName,
          role: "admin",
          department: "Supply Chain",
          isActive: true,
          grantedBy: "system",
          grantedAt: new Date().toISOString(),
          notes: "Auto-provisioned (demo mode)",
        };
        const updated = [meAsAdmin, ...local];
        saveLocalUsers(updated);
        setUsers(updated);
      } else {
        setUsers(local);
      }
      setIsDemoMode(true);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, currentUserEmail, currentUserName]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const currentUser = users.find(
    (u) => u.email.toLowerCase() === currentUserEmail.toLowerCase() && u.isActive,
  );
  const role = currentUser?.role || (isDemoMode ? "admin" : "viewer");
  const level = ROLE_LEVELS[role?.toLowerCase()] || 1;

  const isAdmin = level >= ROLE_LEVELS.admin;
  const isEditor = level >= ROLE_LEVELS.editor;
  const isManager = level >= ROLE_LEVELS.manager;
  const canEdit = level >= ROLE_LEVELS.staff;
  const canManageUsers = isAdmin;
  const canManageEntities = isManager;

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const addUser = useCallback(async (user) => {
    const newUser = {
      ...user,
      id: `local-${Date.now()}`,
      grantedBy: currentUserEmail,
      grantedAt: new Date().toISOString(),
    };
    if (isDemoMode) {
      const updated = [...users, newUser];
      setUsers(updated);
      saveLocalUsers(updated);
      return newUser;
    }
    const token = await getAccessToken(instance, accounts);
    const created = await createUserEntry(token, newUser);
    setUsers((prev) => [...prev, created]);
    return created;
  }, [isDemoMode, users, instance, accounts, currentUserEmail]);

  const editUser = useCallback(async (id, updates) => {
    if (isDemoMode) {
      const updated = users.map((u) => (u.id === id ? { ...u, ...updates } : u));
      setUsers(updated);
      saveLocalUsers(updated);
      return;
    }
    const target = users.find((u) => u.id === id);
    if (!target?._spItemId) throw new Error("No SharePoint ID for user");
    const token = await getAccessToken(instance, accounts);
    await updateUserEntry(token, target._spItemId, { ...target, ...updates });
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...updates } : u)));
  }, [isDemoMode, users, instance, accounts]);

  const removeUser = useCallback(async (id) => {
    if (isDemoMode) {
      const updated = users.filter((u) => u.id !== id);
      setUsers(updated);
      saveLocalUsers(updated);
      return;
    }
    const target = users.find((u) => u.id === id);
    if (!target?._spItemId) throw new Error("No SharePoint ID for user");
    const token = await getAccessToken(instance, accounts);
    await deleteUserEntry(token, target._spItemId);
    setUsers((prev) => prev.filter((u) => u.id !== id));
  }, [isDemoMode, users, instance, accounts]);

  const value = {
    users,
    currentUser,
    currentUserEmail,
    currentUserName,
    role,
    isAdmin,
    isEditor,
    isManager,
    canEdit,
    canManageUsers,
    canManageEntities,
    loading,
    isDemoMode,
    spError,
    addUser,
    editUser,
    removeUser,
    refresh: loadUsers,
  };

  return (
    <UserAccessContext.Provider value={value}>
      {children}
    </UserAccessContext.Provider>
  );
}
